import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { expensesApi as eapi, formatCents } from '../../../api/expenses.js';
import { dmsApi } from '../../../api/dms.js';
import { useAuth } from '../../../contexts/AuthContext.jsx';
import DiscussThread from '../../messenger/DiscussThread.jsx';

const CATEGORIES = ['travel', 'meals', 'lodging', 'transport', 'mileage', 'supplies', 'software', 'other'];

/**
 * Expense report workbench: review/add lines, see policy-violation badges, upload
 * receipts, and drive the lifecycle (submit → approve/reject → reimburse).
 * Re-runs check-policy after edits so violation badges stay fresh.
 */
export default function ReportDetailPage() {
  const { id } = useParams();
  const { can } = useAuth();
  const [report, setReport] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // add-line form
  const [date, setDate] = useState('');
  const [category, setCategory] = useState('meals');
  const [amount, setAmount] = useState('');
  const [merchant, setMerchant] = useState('');

  // Load the report and refresh violation state via check-policy. check-policy
  // returns the same { report, lines } shape augmented with per-line violation.
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const detail = await eapi.getReport(id);
      setReport(detail?.report || null);
      setLines(detail?.lines || []);
      try {
        const checked = await eapi.checkPolicy(id);
        if (checked?.report) setReport(checked.report);
        if (checked?.lines) setLines(checked.lines);
      } catch {
        // check-policy is best-effort for badge refresh; ignore its failure.
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  async function act(label, fn) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (e) {
      const data = e.response?.data;
      const reasons = data?.errors?.reasons || data?.details?.reasons;
      const detail = Array.isArray(reasons) && reasons.length ? `: ${reasons.join('; ')}` : '';
      setError((data?.message || `${label} failed`) + detail);
    } finally {
      setBusy(false);
    }
  }

  async function addLine(e) {
    e.preventDefault();
    if (!date || !category) return;
    await act('Add line', async () => {
      const body = { date, category, merchant: merchant || undefined };
      if (amount !== '') body.amountCents = Math.round(Number(amount) * 100);
      await eapi.addLine(id, body);
      setDate(''); setCategory('meals'); setAmount(''); setMerchant('');
    });
  }

  async function uploadReceipt(lineId, file) {
    if (!file) return;
    await act('Upload receipt', async () => {
      const doc = await dmsApi.upload(file, { name: file.name });
      const docId = doc?.document?.id || doc?.id;
      await eapi.updateLine(lineId, { receiptDocumentId: docId });
    });
  }

  const status = report?.status;
  const cur = report?.currency || '';
  const totalCents = lines.reduce((sum, l) => sum + Number(l.amount_cents || 0), 0);
  const editable = status === 'draft' || status === 'rejected';

  return (
    <section data-testid="expenses-report-detail-page" data-loading={loading || busy ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>{report ? report.title : '…'}</h1>
          <p className="muted">
            <Link to="/expenses/reports">← All reports</Link>
            {report && <> · Status: <span data-testid="expenses-report-status">{status}</span></>}
          </p>
        </div>
        <div className="badge" data-testid="expenses-report-total">{formatCents(totalCents, cur)}</div>
      </header>

      {error && <div className="alert error" data-testid="expenses-report-detail-error">{error}</div>}

      <div className="card actions">
        {editable && (
          <button className="btn primary" data-testid="expense-submit" disabled={busy} onClick={() => act('Submit', () => eapi.submitReport(id))}>Submit</button>
        )}
        {status === 'submitted' && (
          <>
            <button className="btn primary" data-testid={`expense-approve-${id}`} disabled={busy} onClick={() => act('Approve', () => eapi.approveReport(id))}>Approve</button>
            <button className="btn ghost" data-testid="expenses-report-reject" disabled={busy} onClick={() => act('Reject', () => eapi.rejectReport(id))}>Reject</button>
          </>
        )}
        {status === 'approved' && (
          <button className="btn primary" data-testid="expense-reimburse" disabled={busy} onClick={() => act('Reimburse', () => eapi.reimburseReport(id, { via: 'ap' }))}>Reimburse</button>
        )}
      </div>

      <div className="card">
        <h3>Lines</h3>
        <table className="data-table" data-testid="expenses-line-list">
          <thead><tr><th>Date</th><th>Category</th><th>Merchant</th><th>Amount</th><th>Receipt</th><th>Policy</th></tr></thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.id} data-testid={`expenses-line-row-${l.id}`}>
                <td>{String(l.date).slice(0, 10)}</td>
                <td>{l.category}</td>
                <td>{l.merchant || '—'}</td>
                <td>{formatCents(l.amount_cents, cur)}</td>
                <td>
                  {l.receipt_document_id ? <span className="muted">attached</span> : '—'}
                  {editable && (
                    <input
                      type="file"
                      data-testid={`expenses-line-receipt-${l.id}`}
                      onChange={(e) => uploadReceipt(l.id, e.target.files?.[0])}
                      disabled={busy}
                    />
                  )}
                </td>
                <td>
                  {l.violation && (
                    <span className="badge error" data-testid={`expense-violation-${l.id}`}>
                      {(l.violation.issues || []).map((iss) => iss.message).join('; ')}
                    </span>
                  )}
                </td>
              </tr>
            ))}
            {!loading && lines.length === 0 && <tr><td colSpan={6} className="muted">No lines yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {editable && (
        <form onSubmit={addLine} className="card form-card row-form">
          <label>Date
            <input type="date" data-testid="expenses-line-date-input" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
          <label>Category
            <select data-testid="expenses-line-category-input" value={category} onChange={(e) => setCategory(e.target.value)}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>Amount
            <input type="number" step="0.01" data-testid="expenses-line-amount-input" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
          </label>
          <label>Merchant
            <input data-testid="expenses-line-merchant-input" value={merchant} onChange={(e) => setMerchant(e.target.value)} placeholder="merchant" />
          </label>
          <div className="actions">
            <button className="btn primary" type="submit" data-testid="expense-line-add" disabled={busy || !date}>Add line</button>
          </div>
        </form>
      )}

      {can('messenger_thread', 'read') && report && (
        <div className="card" data-testid="expense-discussion">
          <h3>Discussion</h3>
          <DiscussThread subjectType="expense_report" subjectId={id} subjectTitle={report.title || 'Expense report'} shareUrl={`/expenses/reports/${id}`} />
        </div>
      )}
    </section>
  );
}
